import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const ThemeContext = createContext(null);

const LS_KEY = 'fb_theme';
const VALID_THEMES = ['light', 'dark'];
const DEFAULT_THEME = 'light';

function readInitial() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (VALID_THEMES.includes(v)) return v;
  } catch {}
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }) {
  const { currentUser } = useAuth();
  const [theme, setThemeState] = useState(readInitial);

  // Reflect the active theme on <html> so Tailwind's `dark:` variant applies
  // app-wide, including to portals/modals rendered outside the React root.
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    // Keep the browser chrome (iOS Safari address/status bar tint) in step
    // with the app background so dark mode doesn't show a light band.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#0d1520' : '#247BA0');
  }, [theme]);

  // Reconcile with the user's saved preference once auth data is available.
  useEffect(() => {
    const pref = currentUser?.themePreference;
    if (VALID_THEMES.includes(pref) && pref !== theme) {
      setThemeState(pref);
      try {
        localStorage.setItem(LS_KEY, pref);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.themePreference]);

  // User-initiated change: switch, persist (localStorage + Firestore).
  const setTheme = useCallback(
    (next) => {
      if (!VALID_THEMES.includes(next)) return;
      setThemeState(next);
      try {
        localStorage.setItem(LS_KEY, next);
      } catch {}
      if (currentUser?.uid) {
        updateDoc(doc(db, 'users', currentUser.uid), { themePreference: next }).catch((e) =>
          console.warn('Failed to save theme preference:', e)
        );
      }
    },
    [currentUser?.uid]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = { theme, setTheme, toggleTheme };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Safe fallback if used outside the provider.
    return { theme: DEFAULT_THEME, setTheme: () => {}, toggleTheme: () => {} };
  }
  return ctx;
}
